import React from "react";

import { useTranslation } from "lib/i18n/use-translation";

import styles from "./business-impact.module.scss";
import { formatScore } from "../resilience/resilience-view";
import { formatMultiple } from "./business-impact-view";
import { BusinessImpactSample, DomainId } from "./sample";

const K = "admin:companyDashboard.businessImpact.risks";

// The Resilience tab already names its domains; reuse those translations.
const RESILIENCE_DOMAIN_KEY: Record<DomainId, string> = {
  movement: "movement",
  workCapacity: "work_stress",
  sleep: "sleep",
};

const BusinessRiskInsights = ({ sample }: { sample: BusinessImpactSample }) => {
  const { t, i18n } = useTranslation(["admin"]);
  const locale = i18n.language;
  const { riskMultipliers, risk } = sample;
  const domainName = (id: DomainId) =>
    t(`admin:companyDashboard.resilience.domains.${RESILIENCE_DOMAIN_KEY[id]}`);
  const pair = (one: string, other: string) =>
    t("admin:companyDashboard.resilience.read.pair", {
      first: one,
      second: other,
    });
  const callout = () => {
    if (risk.kind === "general") {
      const [first, second] = risk.domains.map(domainName);
      return t(`${K}.prioritiesGeneral`, { domains: pair(first, second) });
    }
    const [first, second] = risk.domains.map(({ id, score }) =>
      t("admin:companyDashboard.resilience.read.domainScore", {
        domain: domainName(id),
        score: formatScore(score, locale),
      })
    );
    const [firstDepartment, secondDepartment] = risk.departments;
    return t(`${K}.priorities`, {
      domains: pair(first, second),
      departments: pair(firstDepartment, secondDepartment),
    });
  };

  return (
    <section className={styles["card"]}>
      <div>
        <h3 className={styles["section-heading"]}>{t(`${K}.title`)}</h3>
        <p className={styles["subheading"]}>{t(`${K}.subtitle`)}</p>
      </div>
      <p className={styles["callout"]}>{callout()}</p>
      <ul className={styles["risks"]}>
        {riskMultipliers.map(({ id, multiplier }) => (
          <li key={id} className={styles["risk"]}>
            <h4 className={styles["risk-title"]}>
              {t(`${K}.items.${id}.title`)}
            </h4>
            <p className={styles["risk-multiple"]}>
              {formatMultiple(multiplier, locale)}
              <span className={styles["risk-x"]}>
                {t(
                  "admin:companyDashboard.businessImpact.summary.multipleSign"
                )}
              </span>
            </p>
            <p className={styles["risk-text"]}>{t(`${K}.items.${id}.text`)}</p>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default BusinessRiskInsights;
